import React, { useEffect, useState } from 'react';
import FormErrorsArray from './FormErrorsArray';
import FormLoading from './FormLoading';
import { useFormDebug } from './FormDebugProvider';

/**
 * FormAccessEvaluator is a React component that manages access control for forms.
 *
 * Props:
 * - hasAccess: (function | boolean | null)
 *    - If null: grants access by default.
 *    - If boolean: grants or denies access based on its value.
 *    - If function: asynchronously evaluates access, expects a function returning a Promise
 *      that resolves to an object with either a `data` (boolean) or `error` property.
 * - children: React nodes to render if access is granted.
 *
 * Behavior:
 * - Shows a loading indicator while evaluating access.
 * - Displays error messages if access evaluation fails.
 * - Renders children if access is granted.
 * - Shows an access denied message if access is not granted.
 */

function FormAccessEvaluator({ hasAccess = null, children }) {
  const debug = useFormDebug();
  const [access, setAccess] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialize = async () => {
    setLoading(true);
    try {
      if (hasAccess === null) {
        setAccess(true);
      } else if (typeof hasAccess === 'function') {
        const accessFound = await hasAccess();
        if (accessFound.data) {
          setAccess(accessFound?.data);
        } else if (accessFound?.error) {
          setErrors(accessFound?.error);
        } else {
          setErrors([
            {
              message: 'Could not fetch form schema',
            },
          ]);
        }
      } else if (typeof hasAccess === 'boolean') {
        setAccess(hasAccess);
      } else {
        setErrors([
          {
            message:
              'FormAccessProvider: hasAccess should be a function, boolean or null value',
          },
        ]);
      }
    } catch (err) {
      setErrors([{ message: err?.message }]);
    }

    setLoading(false);
  };

  useEffect(() => {
    debug.addStep('FormAccessEvaluator');
    initialize();
  }, [hasAccess]);

  if (loading) {
    return <FormLoading>Evaluating Form Access</FormLoading>;
  }

  if (Array.isArray(errors)) {
    if (errors.length > 0) {
      return <FormErrorsArray errors={errors} />;
    }
  } else if (errors) {
    return <FormErrorsArray errors={[{ message: JSON.stringify(errors) }]} />;
  }

  if (!access) {
    return (
      <FormErrorsArray
        errors={[{ message: 'Your do not have access to this form' }]}
      />
    );
  }

  return children;
}

export default FormAccessEvaluator;
